import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { consumerApi } from "@/lib/consumerApi";

function normalizeDeviceCode(value) {
  const compact = String(value || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 8);
  return compact.length > 4 ? `${compact.slice(0, 4)}-${compact.slice(4)}` : compact;
}

function approvalError(error, productName) {
  if (error?.response?.status === 400) {
    return `This device code has expired or has already been used. Start activation again in ${productName}.`;
  }
  if (error?.response?.status === 401) {
    return "Your account session has ended. Sign in again, then retry this activation.";
  }
  if (error?.response?.status === 403) {
    return "Your account session needs refreshing. Reload the page, then retry this activation.";
  }
  return "This device request could not be completed. No device was approved; please try again.";
}

export default function DeviceApprovalCard({ productName = "SecureShield or SecureShield Pro", onApproved }) {
  const inputId = useId();
  const headingId = useId();
  const [searchParams] = useSearchParams();
  const initialCode = useMemo(() => normalizeDeviceCode(searchParams.get("code")), [searchParams]);
  const [code, setCode] = useState(initialCode);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState("");
  const [error, setError] = useState("");
  const [refreshError, setRefreshError] = useState(false);

  useEffect(() => {
    setCode(initialCode);
    setResult("");
    setError("");
    setRefreshError(false);
  }, [initialCode]);

  const decide = useCallback(async (approve) => {
    const normalized = normalizeDeviceCode(code);
    setCode(normalized);
    setResult("");
    setError("");
    if (!/^[BCDFGHJKLMNPQRSTVWXZ]{4}-[BCDFGHJKLMNPQRSTVWXZ]{4}$/.test(normalized)) {
      setError(`Enter the eight-character code shown in ${productName}.`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await consumerApi.post("/v1/device-sessions/complete", {
        user_code: normalized,
        approve,
      });
      if (data?.status !== (approve ? "approved" : "denied")) {
        throw new Error("Unexpected device authorization response");
      }
      setResult(data.status);
      if (approve && onApproved) {
        try { await onApproved(); } catch { setRefreshError(true); }
      }
    } catch (requestError) {
      setError(approvalError(requestError, productName));
    } finally {
      setBusy(false);
    }
  }, [code, onApproved, productName]);

  return (
    <section className="border border-electric/30 bg-white p-7" aria-labelledby={headingId} data-testid="device-approval-card">
      <ShieldCheck className="h-6 w-6 text-electric" strokeWidth={1.7} aria-hidden="true" />
      <div className="lf-eyebrow mt-4">Secure device activation</div>
      <h4 id={headingId} className="mt-2 font-display text-2xl font-bold">Approve this computer?</h4>
      <p className="mt-3 text-sm text-ink-600">
        Only continue if this code matches the one shown in {productName} on a computer you control. Approval does not share your website password.
      </p>
      <label className="mt-5 block text-sm font-semibold text-ink-800" htmlFor={inputId}>
        Device code
      </label>
      <input
        id={inputId}
        className="mt-2 w-full border border-ink-200 px-4 py-3 font-mono uppercase tracking-[.2em]"
        value={code}
        onChange={(event) => setCode(normalizeDeviceCode(event.target.value))}
        autoComplete="one-time-code"
        inputMode="text"
        maxLength={9}
        disabled={busy || Boolean(result)}
        data-testid="device-approval-code"
      />
      {!result ? (
        <div className="mt-5 flex flex-wrap gap-3">
          <button className="lf-btn-primary" type="button" onClick={() => decide(true)} disabled={busy} data-testid="device-approval-approve">
            {busy ? "Checking…" : "Approve computer"}
          </button>
          <button className="lf-btn-secondary" type="button" onClick={() => decide(false)} disabled={busy} data-testid="device-approval-deny">
            Deny request
          </button>
        </div>
      ) : null}
      {result === "approved" ? (
        <p className="mt-4 text-sm text-green-700" role="status">This computer is approved. Return to {productName} to finish activation.</p>
      ) : null}
      {result === "denied" ? (
        <p className="mt-4 text-sm text-ink-700" role="status">The device request was denied. No licence was issued.</p>
      ) : null}
      {refreshError ? <p role="status" className="mt-3 text-sm text-ink-600">Approval succeeded. Refresh device status after finishing activation in the app.</p> : null}
      {error ? <p className="mt-4 text-sm text-red-600" role="alert">{error}</p> : null}
    </section>
  );
}

